import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';

import { Alert, AlertDescription, Margin, Text } from '@zapier/design-system';

import Link from 'app/common/components/Link';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import ResubmissionSteps from 'app/developer-v3/components/ResubmissionSteps';
import { ReviewStatus } from 'app/developer-v3/types/statuses';
import { getAppUrl } from 'app/developer-v3/utils';
import LaunchProgress, {
  Status,
} from 'app/developer-v3/components/LaunchProgress';
import { BASE_DOC_URL_VB, IssueStatus } from 'app/developer-v3/constants';
import {
  CurrentVersionReadOnlyReason,
  PublishingForm as PublishingFormType,
  useCurrentVersionReadOnlyReason,
  useNotify,
  usePublishingForm,
  useQueryImplementation,
  useQueryIntegrationReview,
  useQueryIssues,
  useSidebarRightToPublish,
  useUpdateAdminContactType,
} from 'app/developer-v3/hooks';

import PublishingForm from './PublishingForm';
import PublishingLoader from './PublishingLoader';
import { BlockerType } from './constants';
import { getBlocker } from './utils';

import type { SemVer } from 'app/developer-v3/types/version';

type Props = {
  integrationId: number;
  integrationName: string;
  isDisabled: boolean;
  status: Status;
  version: SemVer;
  versions: SemVer[];
  versionsBlocked: Array<string>;
};

function PublishingPage({
  integrationId,
  integrationName,
  status,
  version,
  versions,
  versionsBlocked,
}: Props) {
  const [versionToPublish, setVersionToPublish] = useState(version);

  const notify = useNotify();

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();

  const queryIssues = useQueryIssues({
    actions: [],
    integrationId,
    orderBy: 'priority',
    orderDirection: 'ASC',
    page: 1,
  });
  const { issues = [] } = queryIssues.data ?? {};

  const queryImplementation = useQueryImplementation({
    integrationId,
    version,
  });
  const implementationId = queryImplementation.data?.id ?? NaN;

  const queryIntegrationReview = useQueryIntegrationReview({
    integrationId,
  });
  const reviewStatus = queryIntegrationReview.data?.status;

  useEffect(() => {
    if (reviewStatus === ReviewStatus.inReview) {
      // TODO: Gor for client-side routing or render an warning page saying the
      // integration is being reviewed.
      // For some reason, it hangs for quite a while if we do client-side
      // routing at the moment. Reloading the browser is a quick fix. It's
      // expensive per se, but rarely happens.
      window.location.href = getAppUrl(integrationId);
    }
  }, [integrationId, reviewStatus]);

  const {
    isLoading: isLoadingPublishingForm,
    isUpdating: isUpdatingPublishingForm,
    publishingForm,
    submit: submitPublishingForm,
    update: updatePublishingForm,
  } = usePublishingForm({ integrationId });

  const { mutate: updateAdminContactType } = useUpdateAdminContactType();

  const isLoading =
    queryImplementation.isLoading ||
    queryIssues.isLoading ||
    queryIntegrationReview.isLoading ||
    isLoadingPublishingForm;

  const hasUnresolvedIssues = issues.some(
    issue => issue.isBlocking && issue.status !== IssueStatus.resolved
  );
  const isVersionBlocked = versionsBlocked.includes(version);

  const blockerType = hasUnresolvedIssues
    ? BlockerType.loki
    : isVersionBlocked && BlockerType.version;
  const blocker = !isLoading && getBlocker(integrationId)[blockerType];

  const onSave = async (publishingFormNext: PublishingFormType) => {
    await updatePublishingForm({
      implementationId,
      publishingForm: publishingForm.id
        ? { ...publishingFormNext, id: publishingForm.id }
        : publishingFormNext,
    });

    const {
      marketingContactEmail,
      securityContactEmail,
      technicalContactEmail,
    } = publishingFormNext;

    if (marketingContactEmail) {
      await updateAdminContactType({
        email: marketingContactEmail,
        integrationId: String(integrationId),
        typeToAdd: 'marketing',
      });
    }

    if (securityContactEmail) {
      await updateAdminContactType({
        email: securityContactEmail,
        integrationId: String(integrationId),
        typeToAdd: 'security',
      });
    }

    if (technicalContactEmail) {
      await updateAdminContactType({
        email: technicalContactEmail,
        integrationId: String(integrationId),
        typeToAdd: 'technical',
      });
    }
  };

  const { validate: validateThenSubmit } = useSidebarRightToPublish({
    onSuccess: async () => {
      // setIsSubmitting(false);
      await submitPublishingForm({ publishingForm, version: versionToPublish });
      // TODO: We reload the browser for now because the integration's status
      // would've changed after submitting the form for review. But the status
      // is stored in the Entity (Redux store) and not updated. We'd need to
      // use Entity to update the store for getting the new status. Technically,
      // while it's totally feasible, we don't double down on Entity because
      // we're moving away from it. When the status, integration and
      // implementation overall actually, is migrated from Entity to
      // developer-v3/hooks based on react-query, we can do client-side routing
      // without reloading.
      window.location.href = getAppUrl(integrationId);
    },
    onError: () => {
      notify.failure(
        `There was a problem validating your integration version ${version}. Please try again later or contact support.`
      );
    },
    id: integrationId,
    version,
  });

  const onSubmit = async () => {
    // When the user submits the form, if there are known blockers
    // (loki issues or version was already submitted)
    // we show a notification
    if (isEmpty(blocker)) {
      await validateThenSubmit();
    } else {
      notify.failure(blocker, 'publishing-blocked', Infinity);
    }
  };

  return (
    <ManagerLayout title="Publishing">
      {isLoading ? (
        <PublishingLoader />
      ) : reviewStatus === ReviewStatus.pushedBack ? (
        <ResubmissionSteps integrationId={integrationId} />
      ) : (
        <LaunchProgress
          integrationId={integrationId}
          integrationName={integrationName}
          status={status}
        />
      )}
      <Text margin="20px 0px" tag="h2" type="ParagraphHeader4">
        Complete these details to help us get your review started.
      </Text>
      <Margin margin="20px 0px">
        <Alert title="Publishing requirements" variant="banner">
          <AlertDescription>
            These questions must be answered before submitting for review. Clear
            and complete answers help us review faster and get your integration
            published sooner. Review these{' '}
            <Link
              href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements`}
              target="_blank"
            >
              publishing requirements
            </Link>{' '}
            for details about the questions.
          </AlertDescription>
        </Alert>
      </Margin>
      <Margin margin="20px 0px">
        <PublishingForm
          integrationId={integrationId}
          isDisabled={
            isLoadingPublishingForm ||
            isUpdatingPublishingForm ||
            [
              CurrentVersionReadOnlyReason.NOT_ADMIN,
              CurrentVersionReadOnlyReason.PENDING_VERSION_LIFECYCLE,
            ].includes(currentVersionReadOnlyReason)
          }
          onChangeVersion={setVersionToPublish}
          onSave={onSave}
          onSubmit={onSubmit}
          publishingForm={publishingForm}
          versions={versions}
        />
      </Margin>
    </ManagerLayout>
  );
}

export default PublishingPage;
